import { useClientInactivityAlertNotices } from '@app/hooks';
import ClientInactivityAlertWarning from '@app/containers/components/ClientInactivityAlertWarning';
import LicenceTag from '@app/containers/components/LicenceTag';
import PlanTag from '@app/containers/components/PlanTag';
import { ToastUtils } from '@app/data/utils';
import { Card, Colors, Icon, Intent, Tag, Tooltip } from '@blueprintjs/core';
import Avatar from '@components/Avatar';
import CommonInformationBlock from '@components/CommonInformationBlock';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import RouterLink from '@components/RouterLink';
import DevText from '@components/Text';
import { Endpoints, imageHashPreview } from '@data/consts';
import { $task } from '@pages/Task/store/states';
import { LicenceState } from 'dy-frontend-http-repository/lib/data/enums';
import { LicenceResource } from 'dy-frontend-http-repository/lib/modules/Task/resources';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import { TextFormatUtils } from 'dy-frontend-shared/lib/utils';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { HTMLAttributes } from 'react';

export type Props = HTMLAttributes<HTMLDivElement>;

const ClientInformation: React.FC<Props> = (props) => {
    const task = useStore($task);

    // Hook order is fixed, so this runs before the early return below. An empty list is a no-op
    // lookup, and the backend still owns the "Show in dashboard" toggle
    const {
        isDashboardEnabled: isInactivityWarningEnabled,
        noticeByClientId: inactivityNoticeByClientId,
        clearNotice: clearInactivityNotice,
        clearingClientUserId: clearingInactivityClientUserId,
    } = useClientInactivityAlertNotices(task ? [task.user_space.id] : []);

    if (!task) {
        return null;
    }

    // Get user
    const userSpace = task.user_space;

    // Get avatar src
    let avatarSrc: string | null = null;
    if (userSpace.image_hash) {
        avatarSrc = imageHashPreview.userImage(userSpace.image_hash, ImageHashPreviewSize.SM);
    }

    // Only present while the server says notices may be surfaced
    const inactivityNotice = isInactivityWarningEnabled ? inactivityNoticeByClientId[userSpace.id] : undefined;

    const renderClientOverview = () => {
        return (
            <Flex align="center">
                <Avatar
                    className="mr-1"
                    width="35px"
                    height="35px"
                    src={avatarSrc}
                    alt={`${userSpace.first_name} ${userSpace.last_name}`}
                />

                <div>
                    {/* The icon sits inline beside the name, so the avatar / company / plan /
                        last activity block below keeps exactly the geometry it had */}
                    <Flex direction="row" align="center">
                        <RouterLink
                            color={Colors.WHITE}
                            to={Endpoints.CLIENT_TASKS.replace(':clientId', userSpace.id)}
                            fontWeight={600}
                        >
                            {userSpace.first_name} {userSpace.last_name}
                        </RouterLink>

                        {inactivityNotice && (
                            <ClientInactivityAlertWarning
                                className="ml-small"
                                notice={inactivityNotice}
                                isClearing={clearingInactivityClientUserId === userSpace.id}
                                onClear={() => {
                                    clearInactivityNotice(userSpace.id).catch(() => {
                                        ToastUtils.showToast({
                                            message: 'Inactivity notice could not be cleared',
                                            intent: Intent.DANGER,
                                        });
                                    });
                                }}
                            />
                        )}
                    </Flex>

                    <DevText muted>{userSpace.company_name}</DevText>
                </div>
            </Flex>
        );
    };

    const renderBrandProfileInformation = () => {
        const brandProfile = task.brand_profile;

        if (!brandProfile) {
            return null;
        }

        return (
            <div className="mt-2">
                <DevText muted className="mb-small">
                    <Icon className="mr-1" icon="helper-management" size={16} color={Colors.GRAY2} />
                    Brand profile
                </DevText>

                <RouterLink
                    color={Colors.WHITE}
                    fontWeight={600}
                    to={Endpoints.BRAND_PROFILE.replace(':brandProfileId', `${brandProfile.id}`)}
                >
                    {brandProfile.title}
                </RouterLink>
            </div>
        );
    };

    const renderLastActivityInformation = () => {
        return (
            <CommonInformationBlock
                className="mt-2"
                icon="walk"
                title="Last activity"
                text={
                    <Tooltip content={moment(task.metadata.latest_client_activity_at).format('D MMM YYYY, HH:mm')}>
                        {TextFormatUtils.capitalize(moment(task.metadata.latest_client_activity_at).fromNow())}
                    </Tooltip>
                }
            />
        );
    };

    const renderWorkspaceInformation = () => {
        const workspace = userSpace.workspace;

        if (!workspace) {
            return null;
        }

        return (
            <div className="mt-2">
                <DevText muted className="mb-small">
                    <Icon className="mr-1" icon="applications" size={16} color={Colors.GRAY2} />
                    Workspace
                </DevText>

                <RouterLink to={Endpoints.WORKSPACE.replace(':workspaceId', workspace.id)} fontWeight={600}>
                    {workspace.title}
                </RouterLink>
            </div>
        );
    };

    const renderPlanList = () => {
        const licences = userSpace.licences;

        if (licences.length === 0) {
            return null;
        }

        const renderPlanListItem = (licence: LicenceResource, index: number) => {
            const plan = licence.plan;

            return (
                <LicenceTag
                    suspended={licence.state === LicenceState.SUSPENDED}
                    id={plan.id}
                    key={licence.id}
                    className={index === 0 ? '' : 'mt-1'}
                    name={licence.plan.title}
                    quantity={licence.quantity}
                    color={plan.color}
                />
            );
        };

        return (
            <div className="mt-2">
                <DevText muted className="mb-small">
                    <Icon className="mr-1" icon="person" size={16} color={Colors.GRAY2} />
                    Plans
                </DevText>

                {licences.map(renderPlanListItem)}
            </div>
        );
    };

    return (
        <Card {...props}>
            <Heading className="mb-2" type="h4">
                Client
            </Heading>

            {renderClientOverview()}
            {renderBrandProfileInformation()}
            {renderWorkspaceInformation()}
            {renderPlanList()}
            {renderLastActivityInformation()}
        </Card>
    );
};

export default ClientInformation;
